import React from 'react';
import {
    Award,
    GraduationCap,
    Building,
    Star,
    CheckCircle,
    Briefcase,
    Network,
    MapPin,
    Phone,
    Mail
} from 'lucide-react';
import ResponsiveNavbar from './ResponsiveNavbar';

const ProfilePage: React.FC = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Hero Section */}
            <ResponsiveNavbar/>
            <section className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                            👨‍💼 Leadership Profile
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                            Meet Our
                            <span className="bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent"> Visionary Leader</span>
                        </h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                            Driving excellence and innovation in consulting services with over two decades of entrepreneurial expertise
                        </p>
                    </div>
                </div>
            </section>

            {/* Director Profile Section */}
            <section className="py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl overflow-hidden shadow-xl">
                        <div className="grid lg:grid-cols-2 gap-0">
                            {/* Profile Image Section */}
                            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-8 sm:p-12 flex items-center justify-center">
                                <div className="text-center">
                                    <div className="w-48 h-48 sm:w-56 sm:h-56 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <div className="w-40 h-40 sm:w-48 sm:h-48 bg-white/30 rounded-full flex items-center justify-center overflow-hidden">
                                            <img
                                                src='/images/ed-pics.png'
                                                alt="Dr. Abiodun Ogundare - CEO SmeHub Consulting"
                                                className="w-full h-full object-cover rounded-full"
                                            />
                                        </div>
                                    </div>
                                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">Dr. Abiodun Ogundare</h3>
                                    <p className="text-blue-100 text-lg">CEO, SmeHub Consulting Limited</p>
                                    <div className="mt-6 flex justify-center space-x-4">
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-white">20+</div>
                                            <div className="text-blue-100 text-sm">Years Experience</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-white">500+</div>
                                            <div className="text-blue-100 text-sm">Students Helped</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Profile Content */}
                            <div className="p-8 sm:p-12">
                                <div className="mb-8">
                                    <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                                        <Award className="w-6 h-6 mr-2 text-blue-600" />
                                        Professional Profile
                                    </h4>
                                    <p className="text-gray-600 leading-relaxed mb-6">
                                        Dr. Abiodun Ogundare is an experienced entrepreneur with a demonstrated history of working in the
                                        transportation, e-commerce, financial services, consulting, and agricultural sectors. With over 20 years
                                        of entrepreneurial experience, he founded SmeHub Consulting Limited to provide comprehensive consultancy
                                        services in training, management, educational services, and corporate support.
                                    </p>
                                </div>

                                <div className="mb-8">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                        <GraduationCap className="w-5 h-5 mr-2 text-blue-600" />
                                        Education & Qualifications
                                    </h4>
                                    <ul className="space-y-2">
                                        {[
                                            'Doctor of Business Administration (DBA) - Strategic Management, Emas Business School, Moscow',
                                            'Commonwealth Executive MBA (CEMBA) - Commonwealth of Learning, Vancouver, Canada',
                                            'Post Graduate Diploma (PGD) - Financial Management',
                                            'Higher National Diploma - Accountancy',
                                            'Fellow of the Chartered Institute of Administration of Nigeria (FCIA)'
                                        ].map((qualification, index) => (
                                            <li key={index} className="flex items-start text-sm text-gray-700">
                                                <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                                {qualification}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="mb-8">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                        <Briefcase className="w-5 h-5 mr-2 text-blue-600" />
                                        Key Roles & Positions
                                    </h4>
                                    <ul className="space-y-2">
                                        {[
                                            'Co-founder & Vice President - CFL Global Holdings Limited',
                                            'Co-founder & Vice Chairman - T&A Livestock & Processing Farms',
                                            'Non-Executive Director - TMA Engineering Services Limited',
                                            'Non-Executive Director - Equal Twelve Resources Limited',
                                            'Non-Executive Director - KFD Skills Development Academy'
                                        ].map((role, index) => (
                                            <li key={index} className="flex items-start text-sm text-gray-700">
                                                <Building className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                                                {role}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                        <Star className="w-5 h-5 mr-2 text-blue-600" />
                                        Core Competencies
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {[
                                            'Negotiation Strategy',
                                            'Educational Consultancy',
                                            'Strategic Management',
                                            'Business Engineering',
                                            'Change Management',
                                            'Corporate Strategy',
                                            'Organizational Management',
                                            'Business Modelling',
                                            'Market Segmentation'
                                        ].map((skill, index) => (
                                            <span key={index} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Vision & Mission */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12">
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                                <Award className="w-6 h-6 mr-2 text-blue-600" />
                                Leadership Vision
                            </h3>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                "To create a global consulting ecosystem that empowers individuals and organizations to achieve their
                                full potential through innovative solutions, strategic guidance, and unwavering commitment to excellence."
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <Network className="w-5 h-5 text-blue-600 mr-3 mt-1" />
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Global Network</h4>
                                        <p className="text-sm text-gray-600">Building strategic partnerships across continents</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <Star className="w-5 h-5 text-blue-600 mr-3 mt-1" />
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Excellence Focus</h4>
                                        <p className="text-sm text-gray-600">Commitment to delivering exceptional results</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                                <GraduationCap className="w-6 h-6 mr-2 text-blue-600" />
                                Educational Philosophy
                            </h3>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                "Education is the most powerful tool for transformation. Through personalized guidance and strategic
                                support, we unlock doors to global opportunities and lifelong success."
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1" />
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Personalized Approach</h4>
                                        <p className="text-sm text-gray-600">Tailored solutions for each unique journey</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1" />
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Proven Success</h4>
                                        <p className="text-sm text-gray-600">98% success rate in student placements</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-indigo-700">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                        Connect with Dr. Ogundare
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                        Ready to discuss your educational or business goals? Schedule a personal consultation with our CEO.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a href='/consultations' className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg">
                            Schedule Meeting
                        </a>
                        <a href='/contact-us' className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300">
                            Send Message
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-12">
                        <div>
                            <div className="flex items-center space-x-2 mb-6">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">S</span>
                                </div>
                                <span className="text-xl font-bold">SmeHub</span>
                            </div>
                            <p className="text-gray-400 leading-relaxed">
                                Your trusted partner in educational consulting, immigration services, and comprehensive business solutions.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4">Services</h4>
                            <ul className="space-y-2 text-gray-400">
                                <li>Student Enrollment</li>
                                <li>Immigration Services</li>
                                <li>Educational Consulting</li>
                                <li>Business Planning</li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4">Contact Nigeria</h4>
                            <div className="space-y-3 text-gray-400">
                                <div className="flex items-start">
                                    <MapPin className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">2nd floor, Oludaisi Ogundare House, 19/21 Balogun street, Anifowose, Ikeja, Lagos</span>
                                </div>
                                <div className="flex items-center">
                                    <Phone className="w-5 h-5 mr-2" />
                                    <span>+234 812 2654683</span>
                                </div>
                                <div className="flex items-center">
                                    <Mail className="w-5 h-5 mr-2" />
                                    <span>enquiry@smehub.ng</span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4">Contact Canada</h4>
                            <div className="space-y-3 text-gray-400">
                                <div className="flex items-start">
                                    <MapPin className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">4516 Valiant Drive, NW, Calgary, T3A 0Y1, Alberta</span>
                                </div>
                                <div className="flex items-center">
                                    <Phone className="w-5 h-5 mr-2" />
                                    <span>+1 825 935 7540</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
                        <p>&copy; 2025 SmeHub Consulting Limited. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ProfilePage;